import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, AdminClient } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import {
  UsersIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const phaseNames: Record<number, string> = {
  1: 'Business Profile', 2: 'Business Intel', 3: 'Brand Guide', 4: 'ICP',
  5: 'Assets', 6: 'Website', 7: 'Blog', 8: 'Social', 9: 'Messaging', 10: 'Activation',
};

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<any>(null);
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const { impersonate } = useAuthStore();

  useEffect(() => {
    const load = async () => {
      try {
        const [statsData, clientsData] = await Promise.all([
          api.getAdminStats(),
          api.getClients(),
        ]);
        setStats(statsData);
        setClients(clientsData.clients);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = clients.filter(c =>
    c.business_name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-charcoal-800">Admin Dashboard</h1>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Clients', value: stats.total_clients, icon: UsersIcon, color: 'bg-teal-50 text-teal-600' },
            { label: 'Avg Phase', value: stats.avg_phase, icon: ChartBarIcon, color: 'bg-copper-50 text-copper-600' },
            { label: 'Unread Messages', value: stats.unread_messages, icon: ChatBubbleLeftRightIcon, color: 'bg-orange-50 text-orange-600' },
            { label: 'Content Items', value: stats.total_content, icon: DocumentTextIcon, color: 'bg-sage-50 text-sage-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-charcoal-800">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Client Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-charcoal-800">Clients</h2>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-500">Business</th>
                <th className="px-4 py-3 font-medium text-gray-500">Location</th>
                <th className="px-4 py-3 font-medium text-gray-500">Phase</th>
                <th className="px-4 py-3 font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 font-medium text-gray-500">Messages</th>
                <th className="px-4 py-3 font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <Link to={`/admin/clients/${client.id}`} className="font-medium text-charcoal-800 hover:text-copper-600">
                      {client.business_name}
                    </Link>
                    <p className="text-xs text-gray-400">{client.email}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {client.city}{client.state_province ? `, ${client.state_province}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-charcoal-700">
                      <span className="font-semibold">{client.current_phase}</span>
                      <span className="text-xs text-gray-400">/ 10</span>
                    </span>
                    <p className="text-xs text-gray-400">{phaseNames[client.current_phase]}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      client.status === 'onboarding' ? 'bg-blue-100 text-blue-700' :
                      client.status === 'active' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {client.unread_messages > 0 && (
                      <span className="bg-copper-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {client.unread_messages}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/clients/${client.id}`}
                        className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                      >
                        View
                      </Link>
                      <button
                        onClick={() => impersonate(client.id)}
                        className="text-xs text-copper-500 hover:text-copper-600 font-medium flex items-center gap-1"
                      >
                        Login as <ArrowRightIcon className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
